import React, {
  useEffect, useState, useCallback, useRef,
} from 'react';
import { useFormik } from 'formik';
import Select, { createFilter, components } from 'react-select';
import { Badge, Button, Form } from 'react-bootstrap';
import PropTypes from 'prop-types';
import useSearch from '../../hooks/useSearch';

const CustomOption = ({
  isFiltering = false,
  visibleGroups = [],
  ...rest
}) => {
  const { data: { group } } = rest;

  const groupIsVisible = visibleGroups.includes(group);

  return (groupIsVisible || isFiltering) && (
    <components.Option {...rest} />
  );
};

CustomOption.propTypes = {
  isFiltering: PropTypes.bool,
  visibleGroups: PropTypes.arrayOf(PropTypes.string),
};

CustomOption.defaultProps = {
  isFiltering: false,
  visibleGroups: [],
};

const CustomGroup = (props) => (
  <div className="source-filter-group">
    <components.Group {...props} />
    <hr />
  </div>
);

const SourceFilter = () => {
  const [visibleGroups, setVisibleGroups] = useState([]);
  const [isFiltering, setIsFiltering] = useState(false);
  const populatedInitialSelectionFromParams = useRef(false);

  const {
    articleSearch,
    criteria,
    results: {
      resultSources: {
        data: resultSources = [],
        total: totalResultSources,
      } = {},
    },
  } = useSearch();

  const { filteredSources = [] } = criteria;

  const {
    handleBlur,
    handleSubmit,
    isSubmitting,
    setFieldValue,
    values: {
      filteredSources: formFilteredSources,
    } = {},
  } = useFormik({
    enableReinitialize: true,
    initialValues: {
      filteredSources: [],
    },
    validate: () => {},
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async ({ filteredSources: newFilteredSources }, actions) => {
      const sourceValues = newFilteredSources.map((source) => source.value);
      articleSearch({
        ...criteria,
        page: 0,
        filteredSources: sourceValues,
      });
      actions.setSubmitting(false);
    },
  });

  const hasResultSources = resultSources?.length;

  useEffect(() => {
    if (filteredSources.length
        && hasResultSources
        && !populatedInitialSelectionFromParams.current) {
      const resultSourcesFlat = resultSources.map(({ options }) => options).flat();
      const initialFormFilteredSources = filteredSources.map(
        (sourceId) => resultSourcesFlat.find(({
          value,
        }) => value === sourceId),
      );

      setFieldValue('filteredSources', initialFormFilteredSources);

      populatedInitialSelectionFromParams.current = true;
    }
  }, [hasResultSources, setFieldValue, filteredSources]);

  useEffect(() => {
    if (!filteredSources.length) {
      setFieldValue('filteredSources', []);
    }
  }, [filteredSources]);

  const selectStyle = {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    option: (base) => ({ ...base, color: '#000' }),
    control: (base) => ({ ...base, color: '#000' }),
    singleValue: (base) => ({ ...base, color: '#000' }),
    group: (base) => ({ ...base, paddingTop: 0, paddingBottom: 0 }),
  };

  const collapsibleGroupLabel = ({ label: group, options }) => {
    // toggle group visibility when group label is clicked
    const onClick = () => {
      if (!isFiltering) {
        const groupIndex = visibleGroups.findIndex((visibleGroup) => visibleGroup === group);
        const groupIsVisible = groupIndex >= 0;

        if (!groupIsVisible) { // group is collapsed, add it to visible list
          setVisibleGroups((prevGroups) => [
            ...prevGroups,
            group,
          ]);
        } else { // group is already visible, collapse it
          const newVisibleGroups = [...visibleGroups];
          newVisibleGroups.splice(groupIndex); //
          setVisibleGroups(newVisibleGroups);
        }
      }
    };

    return (
      <div style={{ fontSize: '1.75rem', paddingTop: '5px', paddingBottom: '5px' }}>
        <div onClick={onClick} style={{ width: 'fit-content', cursor: 'pointer' }}>
          <span style={{ color: '#0d6efd', fontWeight: 'bold' }}>{group}</span>
          <Badge className="ms-2" size="lg">
            {options?.length?.toLocaleString() || 0}
          </Badge>
        </div>
      </div>
    );
  };

  const formatOptionLabel = ({ label, cnt }) => (
    <div style={{ display: 'flex' }}>
      <div>{label}</div>
      {cnt !== null && (
        <div style={{ marginLeft: '5px' }}>
          <Badge className="text-dark" bg="warning">
            {cnt?.toLocaleString() || null}
          </Badge>
        </div>
      )}
    </div>
  );

  const filterOption = createFilter({ ignoreAccents: false });
  const onSelect = (selected) => setFieldValue('filteredSources', selected);

  const handleInputChange = (input) => {
    if (input && !isFiltering) {
      setIsFiltering(true);
    } else if (!input && isFiltering) {
      setIsFiltering(false);
    }
    return input;
  };

  const onMenuClose = () => {
    setVisibleGroups([]);
    setIsFiltering(false);
  };

  const optionComponent = useCallback((optionProps) => (
    <CustomOption
      {...optionProps}
      isFiltering={isFiltering}
      visibleGroups={visibleGroups}
    />
  ), [isFiltering, visibleGroups]);

  const groupComponent = useCallback((groupProps) => (
    <CustomGroup {...groupProps} />
  ), []);

  return (
    <div className="bg-white text-dark pane w-100 source-filter">
      <h3>
        Filter Sources
        <Badge className="ms-2" bg="primary">
          {totalResultSources}
        </Badge>
      </h3>
      <Form onSubmit={handleSubmit}>
        <Select
          closeMenuOnSelect={false}
          isMulti
          options={resultSources}
          menuPortalTarget={document.body}
          styles={selectStyle}
          onChange={onSelect}
          onBlur={handleBlur}
          value={formFilteredSources}
          maxMenuHeight={600}
          formatOptionLabel={formatOptionLabel}
          formatGroupLabel={collapsibleGroupLabel}
          filterOption={filterOption}
          onInputChange={handleInputChange}
          components={{
            Option: optionComponent,
            Group: groupComponent,
          }}
          onMenuClose={onMenuClose}
        />
        <Button className="mt-3" variant="success" type="submit" size="md" disabled={isSubmitting}>
          Filter Sources
        </Button>
      </Form>
    </div>
  );
};

export default SourceFilter;
